'use client'

import { useState, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import FlagImage from './FlagImage'

// Khai báo global interface để hỗ trợ TypeScript
declare global {
  interface Window {
    google: {
      translate: {
        TranslateElement: new (options: { pageLanguage: string }, containerId: string) => void
      }
    }
    googleTranslateElementInit: () => void
  }
}

// Danh sách ngôn ngữ và mã quốc gia (ISO Alpha-2)
const languages = [
  { code: 'en', name: 'English', flag: 'US' },
  { code: 'vi', name: 'Vietnamese', flag: 'VN' },
  { code: 'ko', name: 'Korean', flag: 'KR' },
  { code: 'ja', name: 'Japanese', flag: 'JP' },
  { code: 'de', name: 'German', flag: 'DE' },
  { code: 'zh-CN', name: 'Chinese', flag: 'CN' },
  { code: 'fr', name: 'French', flag: 'FR' },
]

function GoogleTranslateDropdown() {
  const dropdownRef = useRef<HTMLDivElement>(null) // Tham chiếu đến dropdown
  const [selectedLanguage, setSelectedLanguage] = useState(languages[1]) // Tiếng Việt mặc định
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false) // Trạng thái tải script Google Translate

  useEffect(() => {
    const addGoogleTranslateScript = () => {
      if (
        !document.querySelector(
          'script[src="//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"]'
        )
      ) {
        const script = document.createElement('script')
        script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
        script.async = true
        document.body.appendChild(script)

        window.googleTranslateElementInit = function () {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'vi', // Ngôn ngữ mặc định
            },
            'google_translate_element' // HIDDEN ELEMENT (chỉ để Google Translate hoạt động)
          )
          setIsGoogleLoaded(true)
        }
      }
    }

    addGoogleTranslateScript()
  }, [])

  // Theo dõi sự kiện click bên ngoài để đóng dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false) // Đóng dropdown nếu click bên ngoài
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside) // Lắng nghe sự kiện click
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside) // Cleanup event listener
    }
  }, [isDropdownOpen])

  // Hàm thay đổi ngôn ngữ
  const handleLanguageChange = (lang: (typeof languages)[0]) => {
    setSelectedLanguage(lang)
    setIsDropdownOpen(false)

    if (!isGoogleLoaded) return // Đợi Google Translate tải xong

    const translateCombo = document.querySelector('.goog-te-combo') as HTMLSelectElement
    if (translateCombo) {
      translateCombo.value = lang.code
      translateCombo.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* HIDDEN ELEMENT FOR GOOGLE TRANSLATE */}
      <div id="google_translate_element" style={{ display: 'none' }}></div>

      {/* Dropdown Button */}
      <button
        aria-label="Language button"
        className="flex items-center gap-2 border border-gray-300 rounded-lg p-2"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <FlagImage code={selectedLanguage.flag} alt={`${selectedLanguage.name} flag`} />
        <FontAwesomeIcon icon={faChevronDown} className="text-gray-500 text-sm" />
      </button>

      {/* Dropdown List */}
      {isDropdownOpen && (
        <div className="absolute top-full right-0 mt-2 border border-gray-300 rounded-lg bg-white shadow-lg w-48 z-50">
          {languages.map((lang) => (
            <div
              key={lang.code}
              onClick={() => handleLanguageChange(lang)}
              className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-200 ${
                selectedLanguage.code === lang.code ? 'bg-gray-100' : ''
              }`}
            >
              <FlagImage code={lang.flag} alt={`${lang.name} flag`} />
              <span className="text-sm text-black">{lang.name}</span> {/* 🟢 Đổi thành màu đen */}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default dynamic(() => Promise.resolve(GoogleTranslateDropdown), {
  ssr: false,
}) // Chỉ render ở phía client
